// A small, deterministic, dense two-phase simplex (TGCLOUD-1908).
//
//   minimise  c · x   subject to   A_i · x  (<= | >= | =)  b_i,   x >= 0
//
// Written for the per-node horizontal spacing solve (columnSolve.js), whose
// systems are a few hundred variables and constraints with coefficients in
// {0, ±1, ±t, ±0.5} and right-hand sides in pixels. It is not a general LP
// library: there is no presolve, no sparsity, no bounds other than x >= 0.
//
// Determinism matters more than speed here — the same chart must solve to the
// same widths in every worker and on every reopen — so the pivot rules are
// fixed: Dantzig's most-negative reduced cost while the objective is moving,
// Bland's smallest-index rule once it stalls (which also guarantees termination
// on the heavily degenerate systems the spacing produces). The caller checks the
// returned point against its own constraints and falls back when it must; this
// solver never throws on a solvable input.

const DEFAULT_EPS = 1e-9;
const STALL_LIMIT = 50;

// dst += alpha * src over the first `len` entries.
function axpy(dst, src, alpha, len) {
  for (let j = 0; j < len; j++) dst[j] += alpha * src[j];
}

/**
 * @param {{ n: number, c: ArrayLike<number>, constraints: Array<{ coef: ArrayLike<number>, op: 'le'|'ge'|'eq', rhs: number }> }} problem
 * @param {{ maxPivots?: number, eps?: number }} [options]
 * @returns {{ status: 'optimal'|'infeasible'|'unbounded'|'limit', x?: Float64Array, value?: number, pivots: number }}
 */
export function solveLP(problem, options = {}) {
  const n = problem.n | 0;
  const c = problem.c || [];
  const maxPivots = options.maxPivots ?? 200000;
  const EPS = options.eps ?? DEFAULT_EPS;

  // Normalise every row to a non-negative right-hand side.
  const rows = (problem.constraints || []).map((r) => {
    let coef = Array.from(r.coef, (v) => Number(v) || 0);
    let rhs = Number(r.rhs) || 0;
    let op = r.op;
    if (rhs < 0) {
      coef = coef.map((v) => -v);
      rhs = -rhs;
      op = op === 'le' ? 'ge' : op === 'ge' ? 'le' : 'eq';
    }
    return { coef, rhs, op };
  });
  const m = rows.length;
  if (m === 0) {
    const x = new Float64Array(n);
    return { status: 'optimal', x, value: 0, pivots: 0 };
  }

  let nSlack = 0;
  let nArt = 0;
  for (const r of rows) {
    if (r.op === 'le') nSlack++;
    else if (r.op === 'ge') {
      nSlack++;
      nArt++;
    } else nArt++;
  }
  const N = n + nSlack + nArt; // decision columns; column N holds the RHS
  const W = N + 1;
  const T = new Array(m);
  const basis = new Int32Array(m);
  const isArt = new Uint8Array(N);
  let slackCol = n;
  let artCol = n + nSlack;
  for (let i = 0; i < m; i++) {
    const row = new Float64Array(W);
    const r = rows[i];
    for (let j = 0; j < n; j++) row[j] = r.coef[j] || 0;
    if (r.op === 'le') {
      row[slackCol] = 1;
      basis[i] = slackCol;
      slackCol++;
    } else if (r.op === 'ge') {
      row[slackCol] = -1;
      slackCol++;
      row[artCol] = 1;
      isArt[artCol] = 1;
      basis[i] = artCol;
      artCol++;
    } else {
      row[artCol] = 1;
      isArt[artCol] = 1;
      basis[i] = artCol;
      artCol++;
    }
    row[N] = r.rhs;
    T[i] = row;
  }

  // Objective rows hold reduced costs; entry N holds minus the objective value.
  const z1 = new Float64Array(W); // phase 1: sum of artificials
  const z2 = new Float64Array(W); // phase 2: the caller's objective
  for (let j = 0; j < N; j++) z1[j] = isArt[j] ? 1 : 0;
  for (let j = 0; j < n; j++) z2[j] = Number(c[j]) || 0;
  for (let i = 0; i < m; i++) {
    const b = basis[i];
    if (z1[b] !== 0) axpy(z1, T[i], -z1[b], W);
    if (z2[b] !== 0) axpy(z2, T[i], -z2[b], W);
  }

  const dead = new Uint8Array(m); // redundant rows left with a zero-level artificial
  let pivots = 0;

  const pivot = (r, s) => {
    const pr = T[r];
    const p = pr[s];
    for (let j = 0; j < W; j++) pr[j] /= p;
    pr[s] = 1;
    for (let i = 0; i < m; i++) {
      if (i === r) continue;
      const row = T[i];
      const f = row[s];
      if (f !== 0) {
        axpy(row, pr, -f, W);
        row[s] = 0;
      }
    }
    const f1 = z1[s];
    if (f1 !== 0) {
      axpy(z1, pr, -f1, W);
      z1[s] = 0;
    }
    const f2 = z2[s];
    if (f2 !== 0) {
      axpy(z2, pr, -f2, W);
      z2[s] = 0;
    }
    basis[r] = s;
    pivots++;
  };

  // One phase: iterate to optimality on objective row `z`.
  const run = (z, allowArt) => {
    let stall = 0;
    let lastObj = -z[N];
    for (;;) {
      let s = -1;
      if (stall < STALL_LIMIT) {
        // Dantzig: most negative reduced cost (ties → smallest index).
        let best = -EPS;
        for (let j = 0; j < N; j++) {
          if (!allowArt && isArt[j]) continue;
          if (z[j] < best) {
            best = z[j];
            s = j;
          }
        }
      } else {
        // Bland: smallest index with a negative reduced cost.
        for (let j = 0; j < N; j++) {
          if (!allowArt && isArt[j]) continue;
          if (z[j] < -EPS) {
            s = j;
            break;
          }
        }
      }
      if (s < 0) return 'optimal';
      // Ratio test (ties → smallest basis index, Bland).
      let r = -1;
      let bestRatio = Infinity;
      let bestBasis = Infinity;
      for (let i = 0; i < m; i++) {
        if (dead[i]) continue;
        const a = T[i][s];
        if (a > EPS) {
          const ratio = T[i][N] / a;
          if (
            ratio < bestRatio - 1e-12 ||
            (Math.abs(ratio - bestRatio) <= 1e-12 && basis[i] < bestBasis)
          ) {
            bestRatio = ratio;
            r = i;
            bestBasis = basis[i];
          }
        }
      }
      if (r < 0) return 'unbounded';
      pivot(r, s);
      if (pivots > maxPivots) return 'limit';
      const obj = -z[N];
      if (obj < lastObj - 1e-9) {
        stall = 0;
        lastObj = obj;
      } else stall++;
    }
  };

  if (nArt > 0) {
    const st = run(z1, true);
    if (st !== 'optimal') return { status: st, pivots };
    if (-z1[N] > 1e-6) return { status: 'infeasible', pivots };
    // Drive every artificial still basic (at zero level) out of the basis, or
    // retire its row as redundant when nothing else can take its place.
    for (let i = 0; i < m; i++) {
      if (!isArt[basis[i]]) continue;
      let s = -1;
      for (let j = 0; j < N; j++) {
        if (isArt[j]) continue;
        if (Math.abs(T[i][j]) > EPS) {
          s = j;
          break;
        }
      }
      if (s < 0) {
        dead[i] = 1;
        continue;
      }
      pivot(i, s);
    }
  }

  const st = run(z2, false);
  if (st !== 'optimal') return { status: st, pivots };

  const x = new Float64Array(n);
  for (let i = 0; i < m; i++) {
    if (dead[i]) continue;
    const b = basis[i];
    if (b < n) x[b] = Math.max(0, T[i][N]);
  }
  let value = 0;
  for (let j = 0; j < n; j++) value += (Number(c[j]) || 0) * x[j];
  return { status: 'optimal', x, value, pivots };
}
